import { type FC, useMemo } from "react";

import { Checkbox } from "@/shared/components/ui/checkbox";
import { FieldError } from "@/shared/components/ui/FieldError";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type { PersonContact } from "@/shared/types/estateContact";

const CO_APPLICANTS_KEY = "admin_confirm_admin_co_applicants";

function contactName(c: PersonContact): string {
  return [c.first_name, c.last_name].filter(Boolean).join(" ") || "Unknown";
}

export const AdminCoApplyScreen: FC<FlowCustomComponentProps> = ({
  answers,
  setAnswer,
  errors,
}) => {
  const { user } = useAppContext();
  const { contacts } = useUserContacts();

  const candidates = useMemo(() => {
    return contacts.filter(
      (c) =>
        c.can_serve &&
        c.is_distributee &&
        !(c.email && user?.email && c.email.toLowerCase() === user.email.toLowerCase()),
    );
  }, [contacts, user?.email]);

  const selected: number[] = useMemo(() => {
    const raw = answers[CO_APPLICANTS_KEY];
    if (typeof raw !== "string" || !raw) return [];
    try {
      return JSON.parse(raw) as number[];
    } catch {
      return [];
    }
  }, [answers[CO_APPLICANTS_KEY]]);

  const toggleContact = (contactId: number) => {
    const next = selected.includes(contactId)
      ? selected.filter((id) => id !== contactId)
      : [...selected, contactId];
    setAnswer(CO_APPLICANTS_KEY, JSON.stringify(next));
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <p className="font-sans text-xl font-semibold leading-[30px]">
          Who's applying with you?
        </p>
        <p className="text-sm text-secondary tracking-[-0.28px]">
          Select the family member(s) who will co-apply as administrator. They'll be
          added to the petition as co-petitioners.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {candidates.length === 0 && (
          <p className="text-secondary py-4">
            No family members are eligible to co-apply. Go back and choose a different option.
          </p>
        )}
        {candidates.map((contact) => (
          <label
            key={contact.id}
            className="flex cursor-pointer items-center gap-3 rounded-lg border border-primary p-4 hover:bg-subtle"
          >
            <Checkbox
              checked={selected.includes(contact.id)}
              onCheckedChange={() => toggleContact(contact.id)}
            />
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium">{contactName(contact)}</span>
              {contact.relationship && (
                <span className="text-xs text-secondary capitalize">
                  {contact.relationship.replace(/-/g, " ")}
                </span>
              )}
            </div>
          </label>
        ))}
      </div>
      <FieldError message={errors?.co_applicants} />
    </div>
  );
};
